
class CollectionUtilities extends HTMLElement {
  constructor() {
    super();

    // Section id for the Section Rendering API, so filter/sort/pagination
    // fetch only this section instead of the entire collection page.
    this.sectionId =
      this.closest(".shopify-section")?.id.replace("shopify-section-", "") || "collection-products";

    this.handleFilters();
    this.handleRemoveAllFilters();
    this.adjustFiltersDrawer();
    this.handleSortBy();
    this.handleColsSelectors();
    this.handlePriceSlider();
    this.handleInfinitePagination();
  }

  handleFilters() {
    document.querySelectorAll(".collection-filters form").forEach((form) => {
      form.querySelectorAll("input").forEach((input) => {

        const isTextNumeric =
          input.type === "text" &&
          ["numeric", "decimal"].includes(input.getAttribute("inputmode"));

        let debounceTimer;
        let isApplying = false;

        const applyFilters = async () => {
          if (isApplying) return;
          isApplying = true;

          const params = new URLSearchParams(new FormData(form));
          const url = `${window.location.pathname}?${params.toString()}`;
          window.history.replaceState({}, "", url);

          input.closest("dropdown-component")?.querySelector("button")?.focus();
          await this.load();

          setTimeout(() => {
            isApplying = false;
          }, 0);
        };

        const debouncedApply = () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(applyFilters, 1500);
        };

        input.addEventListener("change", applyFilters);

        if (isTextNumeric) {
          input.addEventListener("input", debouncedApply);
          input.addEventListener("blur", applyFilters);
        }
      });
    });
  }

  handleRemoveAllFilters() {
    document.querySelectorAll("[data-remove-all-filters]").forEach((btn) => {
      const params = new URLSearchParams(window.location.search);

      for (const key of params.keys()) {
        if (key.includes("filter.")) {
          btn.removeAttribute("hidden");
        }
      }

      btn.addEventListener("click", () => {
        for (const key of [...params.keys()]) {
          if (key.includes("filter.")) {
            params.delete(key);
          }
        }

        const url = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, "", url);
        this.load();
      });
    });
  }

  adjustFiltersDrawer() {
    window.addEventListener("dialog:open", (event) => {
      if (event.detail.dialog.id === "dialog-filters") {
        this.querySelectorAll("#dialog-filters details[open]").forEach((details) => {
          const content = details.querySelector(".collapse-content");
          content.style.height = content.scrollHeight + "px";
        });
      }
    });
  }

  handleSortBy() {
    this.querySelectorAll(".collection-sort-by").forEach((sortBy) => {
      sortBy.querySelectorAll("input").forEach((input) => {
        input.addEventListener("change", () => {
          const params = new URLSearchParams(window.location.search);
          params.set("sort_by", input.value);
          params.delete("page");
          const url = `${window.location.pathname}?${params.toString()}`;
          window.history.replaceState({}, "", url);
          this.load();
        });
      });
    });
  }

  handleColsSelectors() {
    this.querySelectorAll(".collection-cols-selector").forEach((selector) => {
      const input = selector.querySelector("input");
      const text = selector.querySelector("p");

      input.addEventListener("input", () => {
        text.textContent = selector.dataset.text.replace("{{ count }}", input.value);
        if (selector.dataset.type === "mobile") {
          document.querySelector('#product-grid-wrapper .product-grid')?.style.setProperty("--mobile-cols", input.value);
          localStorage.setItem("collection-cols-mobile", input.value);
        } else {
          document.querySelector('#product-grid-wrapper .product-grid')?.style.setProperty("--desktop-cols", input.value);
          localStorage.setItem("collection-cols-desktop", input.value);
        }
      });

      if (selector.dataset.type === "mobile" && localStorage.getItem("collection-cols-mobile")) {
        input.value = localStorage.getItem("collection-cols-mobile");
        text.textContent = selector.dataset.text.replace("{{ count }}", localStorage.getItem("collection-cols-mobile"));
      } else if (selector.dataset.type === "desktop" && localStorage.getItem("collection-cols-desktop")) {
        input.value = localStorage.getItem("collection-cols-desktop");
        text.textContent = selector.dataset.text.replace("{{ count }}", localStorage.getItem("collection-cols-desktop"));
      }

      window.addEventListener("collection:loaded", () => {
        if (selector.dataset.type == "mobile" && localStorage.getItem("collection-cols-mobile")) {
          document.querySelector('#product-grid-wrapper .product-grid')?.style.setProperty("--mobile-cols", localStorage.getItem("collection-cols-mobile"));
          text.textContent = selector.dataset.text.replace("{{ count }}", localStorage.getItem("collection-cols-mobile"));
        }
        if (selector.dataset.type == "desktop" && localStorage.getItem("collection-cols-desktop")) {
          document.querySelector('#product-grid-wrapper .product-grid')?.style.setProperty("--desktop-cols", localStorage.getItem("collection-cols-desktop"));
          text.textContent = selector.dataset.text.replace("{{ count }}", localStorage.getItem("collection-cols-desktop"));
        }
      });
    });
  }

  handlePriceSlider() {
    document.querySelectorAll('.price-range-slider input[data-range-type="min"]').forEach((input) => {
      input.addEventListener("input", () => {
        input.closest('.filter-price-group-wrapper').querySelector('[name="filter.v.price.gte"]').value = input.value;
      });
    })

    document.querySelectorAll('.price-range-slider input[data-range-type="max"]').forEach((input) => {
      input.addEventListener("input", () => {
        input.closest('.filter-price-group-wrapper').querySelector('[name="filter.v.price.lte"]').value = input.value;
      });
    });
  }

  handleInfinitePagination() {
    const button = document.querySelector(".infinite-pagination-btn");
    if (!button) return;

    const loadMore = async () => {
      button.disabled = true;

      window.history.replaceState({}, "", button.href);

      // Fetch only this section (Section Rendering API) for the next page.
      const nextUrl = new URL(button.href, window.location.origin);
      nextUrl.searchParams.set("section_id", this.sectionId);

      const response = await fetch(nextUrl.toString());
      const data = await response.text();
      const parser = new DOMParser();
      const newDocument = parser.parseFromString(data, "text/html");

      document.querySelector('#product-grid-wrapper .product-grid').insertAdjacentHTML("beforeend", newDocument.querySelector(".product-grid").innerHTML);
      document.querySelector(".pagination-wrapper").replaceWith(newDocument.querySelector(".pagination-wrapper"));

      document.querySelector("#infinite-pagination-status").textContent = button.dataset.textStatus;
      setTimeout(() => {
        document.querySelector("#infinite-pagination-status").textContent = "";
      }, 4000);

      this.handleInfinitePagination();
    };

    button.addEventListener("click", async (e) => {
      e.preventDefault();
      loadMore();
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(async (entry) => {
          if (entry.isIntersecting) {
            await loadMore();
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
      },
    );

    observer.observe(button);
  }

  async load() {
    if (document.querySelector('#product-grid-wrapper .product-grid')) {
      document.querySelector('#product-grid-wrapper .product-grid').style.opacity = 0.2;
    }

    // Fetch only this section (Section Rendering API) instead of the whole page.
    const url = new URL(window.location.href);
    url.searchParams.set("section_id", this.sectionId);

    const response = await fetch(url.toString());
    const data = await response.text();
    const parser = new DOMParser();
    const newDocument = parser.parseFromString(data, "text/html");

    document.querySelector("#product-grid-wrapper").replaceWith(newDocument.querySelector("#product-grid-wrapper"));

    document.querySelectorAll(".collection-utilities-count").forEach((elem) => {
      elem.textContent = newDocument.querySelector(".collection-utilities-count").textContent;
    });

    document.querySelectorAll(".collection-filters-filter").forEach((elem) => {
      elem.replaceWith(newDocument.querySelector(`.collection-filters-filter[data-filter-label="${elem.dataset.filterLabel}"]`));
    });

    document.querySelectorAll("[data-collection-filter-label]").forEach((elem) => {
      elem.replaceWith(newDocument.querySelector(`[data-collection-filter-label][data-filter-label="${elem.dataset.filterLabel}"]`) || "");
    });

    document.querySelectorAll("[data-view-all-results-btn]").forEach((elem) => {
      elem.textContent = newDocument.querySelector("[data-view-all-results-btn]").textContent;
    });

    this.handleFilters();
    this.handlePriceSlider();
    this.handleInfinitePagination();

    window.dispatchEvent(new CustomEvent("collection:loaded"));
  }
  

  debounce(fn, delay = 300) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}
customElements.define("collection-utilities", CollectionUtilities);

/*
  Collection circles - scroll to active item (mobile)
*/
setTimeout(() => {
  const list = document.querySelector(".collection-circles-list");
  if (!list) return;

  const left = list.querySelector("a.active")?.getBoundingClientRect().left || 0;
  list.scrollBy({ left, behavior: "smooth" });
}, 1000);
